import Dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration, { type Duration } from "dayjs/plugin/duration";
import isBetween from "dayjs/plugin/isBetween";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/pt-br";
import calendar from "dayjs/plugin/calendar";
import type { DayOfWeek } from "@/api/model/common";

Dayjs.locale("pt-br");
Dayjs.extend(updateLocale);
Dayjs.extend(relativeTime);
Dayjs.extend(localizedFormat);
Dayjs.extend(duration);
Dayjs.extend(utc);
Dayjs.extend(isBetween);
Dayjs.extend(localeData);
Dayjs.extend(customParseFormat);
Dayjs.extend(calendar);

export const dayjs = Dayjs;
dayjs.locale("pt-br");
dayjs.updateLocale("pt-br", {
  calendar: {
    lastDay: "[Ontem às] LT",
    sameDay: "[Hoje às] LT",
    nextDay: "[Amanhã às] LT",
    lastWeek: "[último] dddd [às] LT",
    nextWeek: "dddd [às] LT",
    sameElse: "L",
  },
});

export function formatDuration(d: Duration, fmt: "H[h]? mm[m]?") {
  if (fmt === "H[h]? mm[m]?") {
    const h = Math.floor(d.asHours());
    const m = d.minutes();
    if (!h) {
      return d.format("mm[m]");
    }
    if (!m) {
      return `${h}h`;
    }
    return d.format(`[${h}h] mm[m]`);
  }
}

/**
 * Formats a duration in seconds as a compact pt-BR label ("40m", "1h 15m"),
 * falling back to whole minutes.
 *
 * @param seconds - Duration in seconds.
 * @returns The formatted duration label.
 */
export function formatDurationLabel(seconds: number): string {
  const duration = dayjs.duration(seconds, "seconds");
  return formatDuration(duration, "H[h]? mm[m]?") ?? `${Math.max(1, Math.round(duration.asMinutes()))} min`;
}

export function toDate(d: Date | string | number) {
  return typeof d === "object" ? d : new Date(d);
}

export type DateInput = string | number | Dayjs.Dayjs | Date;

export function dayjsTZ(d: DateInput, tz?: string | number) {
  return tz != null ? dayjs(d).utcOffset(tz) : dayjs(d);
}

export type TimeStr = `${number}${number}:${number}${number}:${number}${number}`;

export type DateStr = `${number}${number}${number}${number}-${number}${number}-${number}${number}`;

export function getTimeFromDate(d: DateInput, tz: string | undefined): TimeStr {
  const djs = dayjsTZ(d, tz);
  return djs.format("HH:mm:ss") as TimeStr;
}

export function setTimeInDate(d: DateInput, time: TimeStr, tz: string | number | undefined) {
  const djs = dayjsTZ(d, tz);
  return dayjs(djs.format("YYYY-MM-DDT") + time + djs.format("ZZ"));
}

export function getDateStr(d: DateInput, tz?: string | number): DateStr {
  return dayjsTZ(d, tz).format("YYYY-MM-DD") as DateStr;
}

export function isDateStr(d: string): d is DateStr {
  return /^\d{4}-\d{2}-\d{2}$/.test(d);
}

export function isTimeStr(t: string): t is TimeStr {
  return /^\d{2}:\d{2}:\d{2}$/.test(t);
}

const DAYS_OF_WEEK_MAP: { [key in DayOfWeek]: number } = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

/**
 * Find the next occurrence of a specific time on a given date and optionally day of the week.
 * @param from The starting date to find the next time.
 * @param time The time to find.
 * @param daysOfWeek Optional array of days of the week to restrict the search.
 * @param tz The timezone to use.
 * @returns The next occurrence of the specified time.
 */
export function findNextTime(from: DateInput, time: TimeStr, daysOfWeek?: DayOfWeek[], tz?: string | number) {
  let d = setTimeInDate(from, time, tz);
  if (d.isBefore(dayjsTZ(from, tz))) {
    d = d.add(1, "day");
  }
  if (daysOfWeek && daysOfWeek.length > 0) {
    const dayIndexes = daysOfWeek.map((d) => DAYS_OF_WEEK_MAP[d]).sort((a, b) => a - b);
    while (!dayIndexes.includes(d.day())) {
      d = d.add(1, "day");
    }
  }
  return d;
}

/**
 * Find the previous occurrence of a specific time on a given date.
 * @param from The starting date to find the previous time.
 * @param time The time to find.
 * @param tz The timezone to use.
 * @returns The previous occurrence of the specified time.
 */
export function findPrevTime(from: DateInput, time: TimeStr, tz?: string | number) {
  let d = setTimeInDate(from, time, tz);
  if (d.isAfter(dayjsTZ(from, tz))) {
    d = d.subtract(1, "day");
  }
  return d;
}

export function maxDate(...dates: DateInput[]): Dayjs.Dayjs {
  if (dates.length === 0) {
    throw new Error("maxDate requires at least one date");
  }
  let max = dayjsTZ(dates[0]);
  for (let i = 1; i < dates.length; i++) {
    const d = dayjsTZ(dates[i]);
    if (d.isAfter(max)) {
      max = d;
    }
  }
  return max;
}

export function minDate(...dates: DateInput[]): Dayjs.Dayjs {
  if (dates.length === 0) {
    throw new Error("minDate requires at least one date");
  }
  let min = dayjsTZ(dates[0]);
  for (let i = 1; i < dates.length; i++) {
    const d = dayjsTZ(dates[i]);
    if (d.isBefore(min)) {
      min = d;
    }
  }
  return min;
}
